import { useEffect, useState } from "react";
import api, { formatErr, fmtUsd, fmtPct } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";

export default function BottomTabs({ refresh, onSelectSymbol }) {
  const [tab, setTab] = useState("positions");
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const [p, o, h] = await Promise.all([
        api.get("/trade/positions"),
        api.get("/trade/orders"),
        api.get("/trade/positions/history"),
      ]);
      // Stable ordering so live PnL updates never reshuffle rows (newest first, id tiebreak)
      const byOpened = (a, b) => {
        const t = (b.opened_at || "").localeCompare(a.opened_at || "");
        return t !== 0 ? t : (a.id || "").localeCompare(b.id || "");
      };
      setPositions([...p.data].sort(byOpened));
      setOrders(o.data);
      setHistory(h.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 1500);
    return () => clearInterval(i);
  }, [refresh]);

  const closePos = async (id) => {
    try {
      const { data } = await api.post("/trade/positions/close", { position_id: id });
      toast.success(`Position closed · PnL $${fmtUsd(data.pnl)}`);
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const cancelOrd = async (id) => {
    try {
      await api.delete(`/trade/order/${id}`);
      toast.success("Order cancelled");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const tabBtn = (key, label, count) => (
    <button onClick={() => setTab(key)} data-testid={`bottom-tab-${key}`}
      className={`px-3 sm:px-4 py-1.5 rounded-md text-[11px] sm:text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${tab === key ? "bg-white/[0.06] text-[#E3B341] ring-1 ring-inset ring-white/[0.08]" : "text-[#6B7280] hover:text-white hover:bg-white/[0.06]"}`}>
      {label}{count > 0 && <span className="ml-1 text-[#E3B341]">({count})</span>}
    </button>
  );

  return (
    <div className="h-full flex flex-col bg-[#111827] lg:border-t lg:border-[#1F2937]" data-testid="bottom-tabs">
      <div className="flex gap-1 p-1.5 border-b border-[#1F2937] shrink-0 overflow-x-auto">
        {tabBtn("positions", "Positions", positions.length)}
        {tabBtn("orders", "Open Orders", orders.filter((o) => o.status === "PENDING").length)}
        {tabBtn("history", "History", 0)}
      </div>

      <div className="flex-1 overflow-auto">
        {tab === "positions" && <PositionsView items={positions} onClose={closePos} onChanged={load} onSelectSymbol={onSelectSymbol} />}
        {tab === "orders" && <OrdersView items={orders} onCancel={cancelOrd} />}
        {tab === "history" && <HistoryView items={history} />}
      </div>
    </div>
  );
}

/* ─── Positions ─── */
function PositionsView({ items, onClose, onChanged, onSelectSymbol }) {
  const [editingPos, setEditingPos] = useState(null);
  if (!items.length) return <Empty text="No open positions" />;
  return (
    <>
      {/* Mobile: cards */}
      <div className="lg:hidden divide-y divide-[#1F2937]">
        {items.map((p) => (
          <PositionCard key={p.id} p={p} onClose={onClose} onEdit={() => setEditingPos(p)} onSelectSymbol={onSelectSymbol} />
        ))}
      </div>
      {/* Desktop: table */}
      <table className="hidden lg:table w-full text-xs">
        <thead className="text-[10px] uppercase tracking-widest text-[#9CA3AF] border-b border-[#1F2937]">
          <tr>
            <th className="text-left px-3 py-2">Symbol</th>
            <th className="text-left">Side</th>
            <th className="text-right">Size</th>
            <th className="text-right">Entry</th>
            <th className="text-right">Mark</th>
            <th className="text-right">Lev</th>
            <th className="text-right">Margin</th>
            <th className="text-right">SL / TP</th>
            <th className="text-right">PnL</th>
            <th className="text-right pr-3"></th>
          </tr>
        </thead>
        <tbody className="mono">
          {items.map((p) => (
            <tr key={p.id} onClick={() => onSelectSymbol?.(p.symbol)} className="border-b border-[#1F2937] hover:bg-[#1F2937]/50 cursor-pointer" data-testid={`position-row-${p.id}`}>
              <td className="px-3 py-2 font-semibold">{p.symbol.replace("USDT", "/USDT")}</td>
              <td className={p.side === "BUY" ? "text-[#0ECB81]" : "text-[#F6465D]"}>{p.side === "BUY" ? "LONG" : "SHORT"}</td>
              <td className="text-right">{p.quantity}</td>
              <td className="text-right">{fmtUsd(p.entry_price)}</td>
              <td className="text-right">{fmtUsd(p.mark_price)}</td>
              <td className="text-right text-[#FCD535]">{p.leverage}x</td>
              <td className="text-right">${fmtUsd(p.margin)}</td>
              <td className="text-right text-[10px] text-[#9CA3AF]">
                <span className="text-[#F6465D]">{p.stop_loss ? fmtUsd(p.stop_loss) : "—"}</span>
                {" / "}
                <span className="text-[#0ECB81]">{p.take_profit ? fmtUsd(p.take_profit) : "—"}</span>
              </td>
              <td className={`text-right font-semibold ${p.unrealized_pnl >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                {p.unrealized_pnl >= 0 ? "+" : ""}${fmtUsd(p.unrealized_pnl)} ({fmtPct(p.pnl_pct)})
              </td>
              <td className="text-right pr-3 whitespace-nowrap">
                <Button size="sm" onClick={(e) => { e.stopPropagation(); setEditingPos(p); }} variant="ghost"
                  className="h-7 px-2 text-[#FCD535] hover:bg-[#FCD535]/10 text-[10px]"
                  data-testid={`edit-sltp-${p.id}`}>SL/TP</Button>
                <Button size="sm" onClick={(e) => { e.stopPropagation(); onClose(p.id); }} variant="ghost"
                  className="h-7 px-2 text-[#F6465D] hover:bg-[#F6465D]/10"
                  data-testid={`close-position-${p.id}`}>Close</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editingPos && (
        <SLTPModal
          position={editingPos}
          onClose={() => setEditingPos(null)}
          onSaved={() => { setEditingPos(null); onChanged?.(); }}
        />
      )}
    </>
  );
}

function PositionCard({ p, onClose, onEdit, onSelectSymbol }) {
  const profit = p.unrealized_pnl >= 0;
  return (
    <div onClick={() => onSelectSymbol?.(p.symbol)} className="p-3 space-y-2.5 cursor-pointer active:bg-[#1F2937]/40" data-testid={`position-card-${p.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{p.symbol.replace("USDT", "/USDT")}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${p.side === "BUY" ? "bg-[#0ECB81]/15 text-[#0ECB81]" : "bg-[#F6465D]/15 text-[#F6465D]"}`}>
            {p.side === "BUY" ? "LONG" : "SHORT"}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FCD535]/15 text-[#FCD535] font-semibold mono">{p.leverage}x</span>
        </div>
        <div className={`mono font-semibold text-sm ${profit ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
          {profit ? "+" : ""}${fmtUsd(p.unrealized_pnl)} <span className="text-[11px]">({fmtPct(p.pnl_pct)})</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <Cell label="Size" value={p.quantity} />
        <Cell label="Entry" value={`$${fmtUsd(p.entry_price)}`} />
        <Cell label="Mark" value={`$${fmtUsd(p.mark_price)}`} />
        <Cell label="Margin" value={`$${fmtUsd(p.margin)}`} />
        <Cell label="SL" value={p.stop_loss ? `$${fmtUsd(p.stop_loss)}` : "—"} mute={!p.stop_loss} />
        <Cell label="TP" value={p.take_profit ? `$${fmtUsd(p.take_profit)}` : "—"} mute={!p.take_profit} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button onClick={(e) => { e.stopPropagation(); onEdit(); }} variant="outline"
          className="h-9 border-[#FCD535]/40 bg-[#FCD535]/10 hover:bg-[#FCD535]/20 text-[#FCD535] rounded-sm"
          data-testid={`edit-sltp-${p.id}`}>
          Edit SL / TP
        </Button>
        <Button onClick={(e) => { e.stopPropagation(); onClose(p.id); }} variant="outline"
          className="h-9 border-[#F6465D]/40 bg-[#F6465D]/10 hover:bg-[#F6465D]/20 text-[#F6465D] rounded-sm"
          data-testid={`close-position-${p.id}`}>
          Close
        </Button>
      </div>
    </div>
  );
}

/* ─── SL/TP modal ─── */
function SLTPModal({ position, onClose, onSaved }) {
  const isLong = position.side === "BUY";
  const [sl, setSL] = useState(position.stop_loss ?? "");
  const [tp, setTP] = useState(position.take_profit ?? "");
  const [busy, setBusy] = useState(false);
  const entry = position.entry_price;
  const mark = position.mark_price ?? entry;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/trade/positions/sltp", {
        position_id: position.id,
        stop_loss: sl === "" ? null : parseFloat(sl),
        take_profit: tp === "" ? null : parseFloat(tp),
      });
      toast.success("SL/TP updated");
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="sltp-modal-backdrop"
    >
      <div
        className="bg-[#0A0E17] border border-[#1F2937] rounded-sm w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="sltp-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Stop Loss / Take Profit</p>
            <h3 className="text-lg font-semibold mt-1">
              {position.symbol.replace("USDT", "/USDT")}{" "}
              <span className={isLong ? "text-[#0ECB81]" : "text-[#F6465D]"}>
                {isLong ? "LONG" : "SHORT"}
              </span>
            </h3>
          </div>
          <button onClick={onClose} disabled={busy} className="text-[#9CA3AF] hover:text-white" data-testid="close-sltp-modal">
            <X size={20} />
          </button>
        </div>

        <div className="text-xs text-[#9CA3AF] mb-4 bg-[#111827] border border-[#1F2937] p-2.5">
          Entry: <span className="mono text-white">${fmtUsd(entry)}</span> · Mark:{" "}
          <span className="mono text-white">${fmtUsd(position.mark_price)}</span>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex justify-between mb-1">
              <label className="overline text-[#F6465D]">Stop Loss (USDT)</label>
              {sl && <button onClick={() => setSL("")} className="text-[10px] text-[#9CA3AF] hover:text-white">clear</button>}
            </div>
            <input
              type="number" step="any" value={sl} onChange={(e) => setSL(e.target.value)}
              placeholder={isLong ? `< ${fmtUsd(mark)}` : `> ${fmtUsd(mark)}`}
              className="w-full h-9 px-3 mono bg-[#111827] border border-[#1F2937] focus:border-[#F6465D] outline-none rounded-sm text-sm"
              data-testid="sltp-sl-input"
            />
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              {isLong ? "Below current price — set at/above entry for break-even" : "Above current price — set at/below entry for break-even"}
            </p>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <label className="overline text-[#0ECB81]">Take Profit (USDT)</label>
              {tp && <button onClick={() => setTP("")} className="text-[10px] text-[#9CA3AF] hover:text-white">clear</button>}
            </div>
            <input
              type="number" step="any" value={tp} onChange={(e) => setTP(e.target.value)}
              placeholder={isLong ? `> ${fmtUsd(mark)}` : `< ${fmtUsd(mark)}`}
              className="w-full h-9 px-3 mono bg-[#111827] border border-[#1F2937] focus:border-[#0ECB81] outline-none rounded-sm text-sm"
              data-testid="sltp-tp-input"
            />
            <p className="text-[10px] text-[#9CA3AF] mt-1">
              {isLong ? "Must be ABOVE current price — closes in profit" : "Must be BELOW current price — closes in profit"}
            </p>
          </div>
          <Button
            onClick={submit} disabled={busy}
            className="w-full h-10 bg-[#FCD535] hover:bg-[#FCD535]/90 text-black font-semibold rounded-sm"
            data-testid="sltp-save-button"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, mute = false }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">{label}</span>
      <span className={`mono ${mute ? "text-[#4B5563]" : "text-white"}`}>{value}</span>
    </div>
  );
}

/* ─── Orders ─── */
function OrdersView({ items, onCancel }) {
  if (!items.length) return <Empty text="No orders" />;
  return (
    <>
      <div className="lg:hidden divide-y divide-[#1F2937]">
        {items.map((o) => (
          <div key={o.id} className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{o.symbol.replace("USDT", "/USDT")}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#1F2937] text-[#9CA3AF]">{o.type}</span>
                <span className={`text-[10px] font-semibold ${o.side === "BUY" ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>{o.side}</span>
              </div>
              <span className={`text-[10px] font-semibold ${o.status === "PENDING" ? "text-[#FCD535]" : "text-[#9CA3AF]"}`}>{o.status}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <Cell label="Qty" value={o.quantity} />
              <Cell label="Price" value={`$${fmtUsd(o.price || 0)}`} />
              <Cell label="Lev" value={`${o.leverage}x`} />
            </div>
            {o.status === "PENDING" && (
              <Button onClick={() => onCancel(o.id)} variant="outline"
                className="w-full h-8 border-[#1F2937] bg-transparent hover:bg-[#1F2937] text-[#9CA3AF] rounded-sm"
                data-testid={`cancel-order-${o.id}`}>
                Cancel Order
              </Button>
            )}
          </div>
        ))}
      </div>
      <table className="hidden lg:table w-full text-xs">
        <thead className="text-[10px] uppercase tracking-widest text-[#9CA3AF] border-b border-[#1F2937]">
          <tr>
            <th className="text-left px-3 py-2">Symbol</th>
            <th className="text-left">Type</th>
            <th className="text-left">Side</th>
            <th className="text-right">Qty</th>
            <th className="text-right">Price</th>
            <th className="text-right">Lev</th>
            <th className="text-right">Status</th>
            <th className="text-right pr-3"></th>
          </tr>
        </thead>
        <tbody className="mono">
          {items.map((o) => (
            <tr key={o.id} className="border-b border-[#1F2937]">
              <td className="px-3 py-2 font-semibold">{o.symbol.replace("USDT", "/USDT")}</td>
              <td>{o.type}</td>
              <td className={o.side === "BUY" ? "text-[#0ECB81]" : "text-[#F6465D]"}>{o.side}</td>
              <td className="text-right">{o.quantity}</td>
              <td className="text-right">{fmtUsd(o.price || 0)}</td>
              <td className="text-right text-[#FCD535]">{o.leverage}x</td>
              <td className="text-right">{o.status}</td>
              <td className="text-right pr-3">
                {o.status === "PENDING" && (
                  <Button size="sm" onClick={() => onCancel(o.id)} variant="ghost"
                    className="h-7 px-2 text-[#9CA3AF] hover:bg-[#1F2937]"
                    data-testid={`cancel-order-${o.id}`}><X size={14} /></Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* ─── History (closed positions) ─── */
function formatDuration(openedAt, closedAt) {
  const ms = new Date(closedAt) - new Date(openedAt);
  if (!ms || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function HistoryView({ items }) {
  if (!items.length) return <Empty text="No closed positions yet" />;
  return (
    <>
      {/* Mobile cards */}
      <div className="lg:hidden divide-y divide-[#1F2937]">
        {items.slice(0, 80).map((p) => {
          const profit = (p.pnl || 0) >= 0;
          const pnlPct = p.margin ? (p.pnl / p.margin) * 100 : 0;
          return (
            <div key={p.id} className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{p.symbol.replace("USDT", "/USDT")}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${p.side === "BUY" ? "bg-[#0ECB81]/15 text-[#0ECB81]" : "bg-[#F6465D]/15 text-[#F6465D]"}`}>
                    {p.side === "BUY" ? "LONG" : "SHORT"}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FCD535]/15 text-[#FCD535] font-semibold mono">{p.leverage}x</span>
                </div>
                <div className={`mono font-semibold text-sm ${profit ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                  {profit ? "+" : ""}${fmtUsd(p.pnl)} <span className="text-[10px]">({fmtPct(pnlPct)})</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <Cell label="Size" value={p.quantity} />
                <Cell label="Entry" value={`$${fmtUsd(p.entry_price)}`} />
                <Cell label="Exit" value={`$${fmtUsd(p.exit_price || 0)}`} />
              </div>
              <div className="flex items-center justify-between text-[10px] text-[#9CA3AF] mono pt-1">
                <span>Held {formatDuration(p.opened_at, p.closed_at)}</span>
                <span>{new Date(p.closed_at).toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <table className="hidden lg:table w-full text-xs">
        <thead className="text-[10px] uppercase tracking-widest text-[#9CA3AF] border-b border-[#1F2937]">
          <tr>
            <th className="text-left px-3 py-2">Closed</th>
            <th className="text-left">Symbol</th>
            <th className="text-left">Side</th>
            <th className="text-right">Qty</th>
            <th className="text-right">Entry</th>
            <th className="text-right">Exit</th>
            <th className="text-right">Lev</th>
            <th className="text-right">Held</th>
            <th className="text-right pr-3">PnL</th>
          </tr>
        </thead>
        <tbody className="mono">
          {items.map((p) => {
            const profit = (p.pnl || 0) >= 0;
            const pnlPct = p.margin ? (p.pnl / p.margin) * 100 : 0;
            return (
              <tr key={p.id} className="border-b border-[#1F2937]">
                <td className="px-3 py-2 text-[#9CA3AF]">{new Date(p.closed_at).toLocaleString()}</td>
                <td className="font-semibold">{p.symbol.replace("USDT", "/USDT")}</td>
                <td className={p.side === "BUY" ? "text-[#0ECB81]" : "text-[#F6465D]"}>{p.side === "BUY" ? "LONG" : "SHORT"}</td>
                <td className="text-right">{p.quantity}</td>
                <td className="text-right">{fmtUsd(p.entry_price)}</td>
                <td className="text-right">{fmtUsd(p.exit_price || 0)}</td>
                <td className="text-right text-[#FCD535]">{p.leverage}x</td>
                <td className="text-right text-[#9CA3AF]">{formatDuration(p.opened_at, p.closed_at)}</td>
                <td className={`text-right pr-3 font-semibold ${profit ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                  {profit ? "+" : ""}${fmtUsd(p.pnl)} ({fmtPct(pnlPct)})
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function Empty({ text }) {
  return <div className="text-center py-12 text-sm text-[#9CA3AF]">{text}</div>;
}
